import {
  AutocompleteUniselection,
  DownloaderLoading,
  Icon
} from '@yes.technology/react-toolkit'
import { useId } from 'react'
import { useTranslation } from 'react-i18n-lite'
import useExportPDF from 'report/hooks/useExportPDF/useExportPDF'
import useTableContext from '../../hooks/useTableContext'
import useExportXLS from '../../hooks/useXLSData/useExportXLS'
import {
  FileExportActions,
  LayoutAutocompleteWrapper,
  LayoutContainer
} from './LayoutAndReport.styles'

interface LayoutAndReportProps {
  layoutClassificationUuid?: string
}

export default function LayoutAndReport({
  layoutClassificationUuid
}: LayoutAndReportProps) {
  const { mode, displayLayoutSelection, isShowPDFReports, isShowXLSReport } =
    useTableContext()

  const { t } = useTranslation()

  const layoutSelectionLabelId = useId()

  const reportDownloadClasses = 'btn btn-link p-0'

  const {
    exportXLSReport,
    isLoading: isLoadingXLS,
    hasRequiredReportData: hasRequiredReportDataXLS,
    batchIndex: batchIndexXLS
  } = useExportXLS()
  const {
    exportPDFReport,
    isLoading: isLoadingPDF,
    hasRequiredReportData: hasRequiredReportDataPDF,
    batchProgress: batchProgressPDF,
    populateObjectRelationsProgress: populateObjectRelationsProgressPDF,
    renderProgress: renderProgressPDF
  } = useExportPDF({
    reportType: 'list',
    downloadType: 'single-pdf',
    layoutClassificationUuid
  })

  const downloaderLoadingMessages = {
    title: t('report.wait-a-moment'),
    description: t('report.can-take-some-minutes')
  }

  const isLoading = isLoadingXLS || isLoadingPDF

  if (!isShowPDFReports && !isShowXLSReport) {
    return null
  }

  if (mode !== 'view' && !displayLayoutSelection) {
    return null
  }

  if (isLoadingXLS) {
    return (
      <DownloaderLoading
        {...downloaderLoadingMessages}
        customMessage={t('report.downloader-loading.batch-progress', {
          currentBatch: `${batchIndexXLS}`
        })}
      />
    )
  }

  if (isLoadingPDF) {
    let message = ''

    if (batchProgressPDF.isFetching) {
      message = t('report.downloader-loading.batch-progress', {
        currentBatch: `${batchProgressPDF.currentBatch}`
      })
    }

    if (populateObjectRelationsProgressPDF.isFetching) {
      message = t('report.downloader-loading.populate-relation-progress', {
        currentBatch: `${populateObjectRelationsProgressPDF.currentBatch}`
      })
    }

    if (renderProgressPDF.isRendering) {
      message = t('report.downloader-loading.render-progress', {
        renderProgress: `${renderProgressPDF.currentPdf}/${renderProgressPDF.totalPdfs}`
      })
    }

    return (
      <DownloaderLoading
        {...downloaderLoadingMessages}
        customMessage={message}
      />
    )
  }

  return (
    <LayoutContainer>
      <FileExportActions>
        {!isLoading && hasRequiredReportDataPDF && isShowPDFReports && (
          <button
            type='button'
            className={reportDownloadClasses}
            onClick={exportPDFReport}
            title={t('data-tables.download-pdf-report')}
          >
            <Icon iconName='PDFFile' icSize='large' icColor='sanJuan' />
          </button>
        )}

        {!isLoading && hasRequiredReportDataXLS && isShowXLSReport && (
          <button
            type='button'
            className={reportDownloadClasses}
            onClick={() => exportXLSReport()}
            title={t('data-tables.download-xls-report')}
          >
            <Icon iconName='ExcelFile' icSize='large' icColor='sanJuan' />
          </button>
        )}
      </FileExportActions>

      {displayLayoutSelection && (
        <LayoutAutocompleteWrapper>
          <div className='navigation-slider-title' id={layoutSelectionLabelId}>
            Layout
          </div>

          <AutocompleteUniselection
            aria-labelledby={layoutSelectionLabelId}
            options={[]}
            heightClass='semiSmall'
            disabled
            value=''
          />
        </LayoutAutocompleteWrapper>
      )}
    </LayoutContainer>
  )
}
